import React, { Children, useState, useEffect, useCallback } from 'react'
import { bool, element, node, oneOfType, string } from 'prop-types'

import { placementPropType } from '../tooltip/tooltip-prop-type'
import { ButtonBarContent } from './button-bar-content'
import { getNumberVisibleButtons } from './utils'
import { dropdownPlacementTypesPropType } from './constants'

const propTypes = {
    additionalContent: oneOfType([node, element]),
    children: node,
    className: string,
    dropdownPlacement: dropdownPlacementTypesPropType,
    hideIfEmpty: bool,
    moreButtonTooltip: string,
    moreButtonTooltipPlacement: placementPropType,
    noBackground: bool,
    noPadding: bool,
    testClassNamePostfix: string
}

const defaultProps = {
    dropdownPlacement: 'top-right',
    hideIfEmpty: false,
    noBackground: false,
    noPadding: false,
    testClassNamePostfix: ''
}

/**
 * Wrapper for the button bar content, managing width updates of involved components. Buttons are
 * derived from children. The current width of all buttons is reported back from those buttons and gathered
 * here in an array. If children are changing the button widths are invalid and have to be reset.
 *
 * @param {ReactElement} [props.additionalContent] - additional content (eg. a checkbox) to be rendered
 * @param {ReactElement} [props.children] - The jsx children to be rendered
 * @param {string} [props.className] - Optional classname for the wrapper
 * @param {string} [props.dropdownPlacement] - Optional position of the more button dropdown - default is top-right
 * @param {boolean} [props.hideIfEmpty] - hide empty button bar - default is false
 * @param {string} [props.moreButtonTooltip] - content of the more button tooltip
 * @param {string} [props.moreButtonTooltipPlacement] - position of the more button tooltip
 * @param {boolean} [props.noBackground] - remove background color
 * @param {boolean} [props.noPadding] - remove padding
 * @param {string} [props.testClassNamePostfix] - string appended to the t3sel classname of the nav element
 * @returns {ReactElement}
 */
export function ButtonBar(props) {
    const [buttons, setButtons] = useState(Children.toArray(props.children))
    const [buttonWidths, setButtonWidths] = useState(Children.toArray(props.children).fill(undefined))
    const [moreButtonWidth, setMoreButtonWidths] = useState(0)
    const [availableWidth, setAvailableWidth] = useState(0)
    const [additionalContentWidth, setAdditionalContentWidth] = useState(0)
    const [numberOfVisibleButtons, setNumberOfVisibleButtons] = useState(0)

    const onButtonWidthChanged = useCallback((index, width) => {
        typeof width === 'number' &&
            setButtonWidths(currentWidths =>
                currentWidths.map((currentWidth, widthIndex) => {
                    return index === widthIndex ? Math.ceil(width) : currentWidth
                })
            )
    }, [])

    useEffect(() => {
        // children have changed, reset buttons state
        setButtons(Children.toArray(props.children))
        setButtonWidths(Children.toArray(props.children).fill(undefined))
    }, [props.children])

    useEffect(() => {
        if (buttonWidths.every(width => typeof width === 'number')) {
            const currentNumberOfVisibleButtons = getNumberVisibleButtons(
                availableWidth,
                buttonWidths,
                moreButtonWidth,
                10,
                additionalContentWidth
            )

            if (currentNumberOfVisibleButtons !== numberOfVisibleButtons) {
                setNumberOfVisibleButtons(currentNumberOfVisibleButtons)
            }
        }
    }, [additionalContentWidth, availableWidth, buttonWidths, moreButtonWidth, numberOfVisibleButtons])

    return (
        <ButtonBarContent
            {...{
                ...props,
                numberOfVisibleButtons,
                onAdditionalContentWidthChanged: useCallback(width => setAdditionalContentWidth(width), []),
                onAvailableWidthChanged: useCallback(width => setAvailableWidth(width), []),
                onButtonWidthChanged,
                onMoreButtonWidthChanged: useCallback(width => setMoreButtonWidths(width), [])
            }}
        >
            {buttons}
        </ButtonBarContent>
    )
}

ButtonBar.propTypes = propTypes
ButtonBar.defaultProps = defaultProps
